/**
 * Primitives partagées par toutes les vues.
 * Elles portent la grammaire « planche technique » pour qu'aucune page n'ait à la réécrire.
 */

import { useTranslation } from 'react-i18next';
import {
  BTN_GHOST_SM,
  BTN_PRIMARY,
  DECO_ENTETE,
  EYEBROW,
  PAGE_TITLE,
  PAGE_TITLE_INTERACTIVE,
  PANEL,
  TAG,
} from '../ui/theme.js';

/** Cellule encadrée avec équerres. `padded={false}` pour les contenus qui gèrent leur propre marge. */
export function Panel({ children, className = '', padded = true, ticks = true }) {
  return (
    <section className={`${PANEL} ${padded ? 'p-4' : ''} ${className}`}>
      {ticks && <span className="ticks" aria-hidden="true" />}
      {children}
    </section>
  );
}

/** Titre de section — remplace les `<h3>` gras (design system §3). */
export function Eyebrow({ children, as: Tag = 'h2', className = '' }) {
  return <Tag className={`${EYEBROW} ${className}`}>{children}</Tag>;
}

/**
 * En-tête de page : titre, sous-titre, actions secondaires puis LE bouton primaire, à droite.
 *
 * Deux éléments imbriqués, et ce n'est pas décoratif : le trait 2 px vit sur l'élément
 * EXTÉRIEUR, qui n'est jamais borné, tandis que le contenu est borné par `--largeur-corps`
 * comme le corps de page. Le contrat exiUI (§5 de `coquille.md`) exige un « trait 2 px pleine
 * largeur » collé aux bords ; enfermer l'en-tête entier dans la borne laissait le trait
 * s'arrêter en l'air, avec du papier nu de chaque côté, sur toutes les pages du produit.
 * Le titre reste malgré tout aligné sur le corps, puisque les deux partagent la même borne.
 * `bounded={false}` lève cette borne pour l'espace de travail d'un projet, pleine largeur
 * comme son corps (cf. `PageBody`).
 *
 * Règles de cohérence d'`@exidis/ui` 1.3.0 (coquille.md §5, reference.md §6.1), tenues ICI
 * plutôt que page par page :
 * - la trame `DECO_ENTETE` est posée sur TOUT en-tête, sans option pour la retirer — exiPlan
 *   compose son en-tête avec les classes du paquet plutôt qu'avec `EnTetePage`, il doit donc
 *   reposer lui-même le fragment documenté au-dessus de `PAGE_HEADER` dans `theme.js` ;
 * - `primary` est l'emplacement du SEUL bouton primaire de l'écran (`PrimaryButton`), posé à
 *   droite des `actions`, qui sont toutes en `BTN_GHOST*`.
 */
export function PageHeader({ eyebrow, title, subtitle, meta, actions, primary, bounded = true }) {
  // `PAGE_TITLE` porte `truncate` (`overflow: hidden`) — nécessaire pour un titre TEXTE trop
  // long, mais désastreux pour un titre INTERACTIF (sélecteur d'organisation/équipe, 217:4531) :
  // son menu déroulant, en position absolue, est un DESCENDANT du `<h1>` et se retrouvait rogné
  // par ce même `overflow: hidden`, invisible bien que présent dans le DOM avec les bonnes
  // coordonnées. Un titre non-texte (un composant, donc interactif) perd `truncate` — lui seul
  // sait s'il en a besoin sur son propre libellé interne.
  const titleClassName = typeof title === 'string' ? PAGE_TITLE : PAGE_TITLE_INTERACTIVE;
  return (
    <div className="relative border-b-2 border-line" data-testid="page-header">
      <span className={DECO_ENTETE} aria-hidden="true" />
      <div
        className={`relative flex w-full flex-wrap items-end justify-between gap-3 px-4 py-4 ${
          bounded ? 'mx-auto max-w-(--largeur-corps)' : ''
        }`}
      >
        <div className="min-w-0">
          {eyebrow && <Eyebrow as="p">{eyebrow}</Eyebrow>}
          <h1 className={titleClassName}>{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
          {meta && <div className="mt-2 flex flex-wrap items-center gap-3">{meta}</div>}
        </div>
        {(actions || primary) && (
          <div className="flex flex-wrap items-center gap-2">
            {actions}
            {primary}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LE bouton primaire d'un écran (`BTN_PRIMARY`) — règle de cohérence 1.3.0 d'`@exidis/ui` :
 * un seul par écran, l'action principale, dans l'emplacement `primary` de `PageHeader`.
 * Seule autre place admise : l'action unique d'un écran fermé sans en-tête de page
 * (acceptation d'invitation, activation du compte), qui n'a pas d'autre bouton primaire.
 * Les modales, écrans à part, gardent leur propre bouton de validation (`dialogs/`, `Modal`).
 *
 * Aucune page n'importe `BTN_PRIMARY` directement : c'est ce qui rend « un seul primaire,
 * dans l'en-tête » vérifiable à la lecture d'un seul fichier.
 */
export function PrimaryButton({ className = '', type = 'button', ...props }) {
  return <button type={type} className={`${BTN_PRIMARY} ${className}`.trim()} {...props} />;
}

/**
 * Corps de page borné par la préférence de largeur (`--largeur-corps`).
 *
 * À poser sous un `PageHeader`, jamais autour de lui. Une page qui ne l'emploie pas reste en
 * pleine largeur — c'est le cas voulu de l'espace de travail d'un projet (kanban, Gantt,
 * planning), dont les vues débordent horizontalement et n'ont rien à gagner d'une colonne
 * resserrée. Cette exception est annoncée à l'utilisateur sur l'écran des préférences, comme
 * le fait exiWatch pour son tableau de bord : une borne qui ne s'applique pas partout doit se
 * dire, sinon elle passe pour un défaut.
 */
export function PageBody({ children, className = '' }) {
  return <div className={`mx-auto w-full max-w-(--largeur-corps) ${className}`}>{children}</div>;
}

/**
 * Indicateur d'état d'enregistrement — partagé entre `ProfilePage` et `PreferencesPage`
 * plutôt que dupliqué : mêmes trois états (`loading`, `unsaved`, `saved`), plus `error`.
 *
 * `detail` ajoute un suffixe (« · hh:mm ») — seul `ProfilePage` (207:3129) l'utilise pour
 * dater l'enregistrement ; sans lui, le texte reste identique à avant (`PreferencesPage`).
 */
export function SaveStatus({ state, busy = false, detail }) {
  const { t } = useTranslation();
  const KEYS = {
    loading: 'common.saveStatus.loading',
    unsaved: 'common.saveStatus.unsaved',
    saved: 'common.saveStatus.saved',
    error: 'common.saveStatus.error',
  };
  return (
    <span
      role="status"
      aria-busy={busy}
      className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-faint"
    >
      {t(KEYS[state] ?? state)}
      {detail ? ` · ${detail}` : ''}
    </span>
  );
}

/** Cellule vide — hachures à -45°, jamais un simple espace blanc. */
export function Empty({ children, action }) {
  return (
    <div className="hatch flex min-h-32 flex-col items-center justify-center gap-3 border-2 border-line p-6 text-center">
      <p className="bg-paper px-2 text-sm text-ink-soft">{children}</p>
      {action}
    </div>
  );
}

/**
 * Bandeau affiché quand `useAllTasks` a atteint sa borne de sécurité de pages : la vue montre
 * alors un sous-ensemble des tâches du projet, jamais sans le dire (§ issue B5). Accent + icône
 * + texte — la couleur ne porte jamais seule l'information (design system §9).
 */
export function TruncatedTasksWarning() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex items-center gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
    >
      <span aria-hidden="true">▲</span>
      <p>{t('common.tasksTruncated')}</p>
    </div>
  );
}

/**
 * Bandeau affiché quand une liste complète (équipes, membres, projets…) a atteint la borne de
 * `fetchAllPages` : elle est partielle, et ne doit jamais l'être sans le dire (audit client).
 */
export function TruncatedListWarning() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex items-center gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
    >
      <span aria-hidden="true">▲</span>
      <p>{t('common.listTruncated')}</p>
    </div>
  );
}

/**
 * Échec de chargement d'une vue : message + « Réessayer » quand l'appelant fournit `onRetry`.
 * Accent d'erreur + icône + texte (la couleur ne porte jamais seule l'information). Le détail
 * serveur (`error.message`) est affiché quand il existe, `common.loadError` sert de repli.
 */
export function LoadError({ error, onRetry, className = '' }) {
  const { t } = useTranslation();
  const detail = error?.message;
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center gap-3 border-2 border-statut-erreur bg-statut-erreur-wash px-3 py-2 text-sm text-statut-erreur ${className}`}
    >
      <span aria-hidden="true">▲</span>
      <p className="min-w-0 flex-1">
        {t('common.loadError')}
        {detail ? ` ${detail}` : ''}
      </p>
      {onRetry && (
        <button type="button" className={BTN_GHOST_SM} onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

const STATUS_KEYS = {
  todo: 'status.todo',
  in_progress: 'status.inProgress',
  blocked: 'status.blocked',
  review: 'status.review',
  done: 'status.done',
};

const PRIORITY_KEYS = {
  low: 'priority.low',
  medium: 'priority.medium',
  high: 'priority.high',
  critical: 'priority.critical',
};

const KIND_KEYS = { task: 'kind.task', milestone: 'kind.milestone', phase: 'kind.phase' };

/** Traduit un statut/priorité/type de tâche. `t` vient de `useTranslation()` de l'appelant. */
export const labelForStatus = (status, t) =>
  STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status;
export const labelForPriority = (priority, t) =>
  PRIORITY_KEYS[priority] ? t(PRIORITY_KEYS[priority]) : priority;
export const labelForKind = (kind, t) => (KIND_KEYS[kind] ? t(KIND_KEYS[kind]) : kind);

/**
 * Étiquette de statut. Le libellé est TOUJOURS écrit : la couleur ne porte jamais seule
 * l'information (design system §9, piège du daltonisme).
 * Le seul état mis en avant à l'accent est « en cours » ; les autres restent neutres,
 * pour que l'accent garde sa valeur de signal.
 */
export function StatusTag({ status }) {
  const { t } = useTranslation();
  const emphasised = status === 'in_progress' || status === 'blocked';
  return (
    <span
      className={`${TAG} ${
        emphasised
          ? 'border-accent-trait bg-accent-wash text-accent-ink'
          : 'border-line text-ink-soft'
      }`}
    >
      {status === 'blocked' && <span aria-hidden="true">▲</span>}
      {status === 'done' && <span aria-hidden="true">✓</span>}
      {labelForStatus(status, t)}
    </span>
  );
}

/** Étiquette de priorité — neutre, le nombre de chevrons porte le rang en plus du mot. */
export function PriorityTag({ priority }) {
  const { t } = useTranslation();
  const marks = { low: '', medium: '›', high: '››', critical: '›››' }[priority] ?? '';
  return (
    <span className={`${TAG} border-line text-ink-soft`}>
      {marks && <span aria-hidden="true">{marks}</span>}
      {labelForPriority(priority, t)}
    </span>
  );
}

/** Barre d'avancement, angles droits, remplissage au trait d'accent (indicateur de 4 px). */
export function Progress({ value, label }) {
  const { t, i18n } = useTranslation();
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const percent = new Intl.NumberFormat(i18n.resolvedLanguage, { style: 'percent' }).format(
    clamped / 100,
  );
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-2 w-full border-2 border-line bg-surface"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label || t('common.progress')}
      >
        {/* La largeur EST la donnée : l'avancement en pourcentage, inconnu à la
            compilation. Barre de 4 px : c'est un indicateur, donc le TRAIT d'accent
            (`bg-accent-trait`, l'encre en clair — @exidis/ui 1.7.0), qui suit le thème.
            exiui:allow style-inline — géométrie calculée */}
        <div className="h-full bg-accent-trait" style={{ width: `${clamped}%` }} />
      </div>
      <span className="font-mono text-[11px] tabular-nums text-ink-faint">{percent}</span>
    </div>
  );
}

/** Référence lisible d'une tâche : `CLE-12`. */
export const taskReference = (projectKey, number) => `${projectKey}-${number}`;
